/* ═══════════════════════════════════════════════════════════════════════
   A password box with an eye on it.

   Shared, because there are several — signing up, signing in, choosing a
   new one from a reset link, and proving it before closing an account —
   and a reveal that exists on one of them is worse than none: somebody
   learns the button is there and then hunts for it on the screen that
   hasn't got it.
   ═══════════════════════════════════════════════════════════════════════ */

import { useState } from "react";
import { EyeClosed, EyeOpen } from "./icons";

export /**
 * A password box with an eye on it.
 *
 * Not a `<label>` wrapping everything, which is what Field does — a button
 * inside a label is a button that also does the label's job when clicked,
 * and screen readers announce the pair as one confusing control. So the
 * label points at the input by id and the button sits outside it.
 *
 * Revealing is purely local: the value is in component state either way,
 * nothing is logged, and `autoComplete` stays new-password so a manager
 * still offers to generate one.
 *
 * Each field keeps its own eye rather than sharing a switch. Clicking the
 * first one and watching the second change would be a small surprise, and
 * a form is the wrong place for those.
 */
function PasswordField({
  id,
  label,
  hint,
  value,
  onChange,
  autoComplete = "new-password",
  autoFocus,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange(v: string): void;
  /**
   * `current-password` when signing in, `new-password` when setting one.
   * Getting it wrong makes a password manager offer to generate a fresh
   * password on the sign-in form, which is a small disaster.
   */
  autoComplete?: "current-password" | "new-password";
  autoFocus?: boolean;
  children?: React.ReactNode;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="block">
      <label htmlFor={id} className="term-label" style={{ display: "block", marginBottom: hint ? 2 : 5 }}>
        {label}
      </label>
      {hint && (
        <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)", marginBottom: 5 }}>
          {hint}
        </span>
      )}
      <div style={{ position: "relative" }}>
        <input
          id={id}
          className="field"
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // Room for the eye, so a long password doesn't run under it.
          style={{ paddingRight: 42 }}
        />
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          title={shown ? "Hide" : "Show"}
          style={{
            position: "absolute",
            right: 6,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            padding: "4px 6px",
            cursor: "pointer",
            color: "var(--ink-3)",
            display: "grid",
            placeItems: "center",
            lineHeight: 0,
          }}
        >
          {shown ? <EyeOpen /> : <EyeClosed />}
        </button>
      </div>
      {children}
    </div>
  );
}
